import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { toast } from 'sonner';
import { Check, Copy } from 'lucide-react';
import { AddressField } from '@/components/app/address-field';
import { Field } from '@/components/app/misc';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { slugify, submitNativeForm } from '@/lib/utils';
/**
 * "Criar Página com Modelo": envia para project.store com template_id
 * (formulário nativo, porque a rota redireciona para o construtor em Blade).
 */
export function UseTemplateDialog({ template, onClose, domains, storeUrl, checkSlugUrl, domainsUrl, }) {
    const [domainId, setDomainId] = React.useState(domains[0] ? String(domains[0].id) : '');
    const [title, setTitle] = React.useState('');
    const [slug, setSlug] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);
    React.useEffect(() => {
        if (!template)
            return;
        setTitle('');
        setSlug('');
        setSubmitting(false);
    }, [template]);
    if (!template)
        return null;
    const valid = !!domainId && title.trim().length > 0;
    const submit = (e) => {
        e.preventDefault();
        if (!valid)
            return;
        setSubmitting(true);
        submitNativeForm(storeUrl, { template_id: template.id, domain_id: domainId, title, slug });
    };
    return (_jsx(Dialog, { open: true, onOpenChange: (open) => !open && onClose(), children: _jsxs(DialogContent, { className: "max-h-[calc(100vh-2rem)] max-w-lg overflow-y-auto", children: [_jsxs(DialogHeader, { children: [_jsx(DialogTitle, { children: "Criar p\u00E1gina com modelo" }), _jsxs(DialogDescription, { children: ["Modelo: ", template.title] })] }), _jsxs("form", { id: "use-template-form", onSubmit: submit, className: "grid gap-4 py-2", children: [_jsx(Field, { label: "Nome", htmlFor: "tpl-title", children: _jsx(Input, { id: "tpl-title", required: true, autoFocus: true, value: title, onChange: (e) => {
                                    // Como na tela antiga, o caminho acompanha o nome enquanto não for editado à mão.
                                    if (!slug || slug === slugify(title))
                                        setSlug(slugify(e.target.value));
                                    setTitle(e.target.value);
                                }, placeholder: "Minha P\u00E1gina" }) }), _jsx(AddressField, { domains: domains, domainId: domainId, onDomainChange: setDomainId, slug: slug, onSlugChange: setSlug, checkUrl: checkSlugUrl, addDomainUrl: domainsUrl || undefined, idPrefix: "tpl" }), domainsUrl && domains.length > 0 ? (_jsxs("p", { className: "-mt-2 text-[0.8rem] text-muted-foreground", children: ["Ainda n\u00E3o adicionou seu dom\u00EDnio?", ' ', _jsx("a", { href: domainsUrl, className: "font-medium text-foreground underline underline-offset-4", children: "Clique aqui!" })] })) : null] }), _jsxs(DialogFooter, { className: "gap-2 sm:gap-0", children: [_jsx(Button, { variant: "outline", type: "button", onClick: onClose, children: "Cancelar" }), _jsx(Button, { type: "submit", form: "use-template-form", disabled: !valid || submitting, children: submitting ? 'Criando...' : 'Criar' })] })] }) }));
}
/** "Compartilhe Seu Modelo": link templates?share=código com botão de copiar. */
export function ShareTemplateDialog({ url, onClose }) {
    const [copied, setCopied] = React.useState(false);
    React.useEffect(() => setCopied(false), [url]);
    if (!url)
        return null;
    const copy = () => {
        navigator.clipboard
            .writeText(url)
            .then(() => {
            setCopied(true);
            toast.success('Link copiado!');
        })
            .catch(() => toast.error('Não foi possível copiar. Selecione e copie manualmente.'));
    };
    return (_jsx(Dialog, { open: true, onOpenChange: (open) => !open && onClose(), children: _jsxs(DialogContent, { className: "max-w-lg", children: [_jsxs(DialogHeader, { children: [_jsx(DialogTitle, { children: "Compartilhe seu modelo" }), _jsx(DialogDescription, { children: "Obs.: qualquer pessoa com o link ser\u00E1 capaz de us\u00E1-lo." })] }), _jsx(Field, { label: "Link do modelo", htmlFor: "share-link", children: _jsx(Input, { id: "share-link", readOnly: true, value: url, onClick: (e) => e.currentTarget.select(), className: "font-mono text-[13px]" }) }), _jsxs(DialogFooter, { className: "gap-2 sm:gap-0", children: [_jsx(Button, { variant: "outline", type: "button", onClick: onClose, children: "Cancelar" }), _jsxs(Button, { type: "button", onClick: copy, children: [copied ? _jsx(Check, {}) : _jsx(Copy, {}), copied ? 'Copiado!' : 'Copiar link'] })] })] }) }));
}
